import { useState, useEffect, useCallback, useRef } from 'react';
import { generatePassword, PASSWORD_CLASS_WEIGHTS, type PasswordOptions } from '../../crypto/crypto';
import { constraintsFromGeneratorOptions, generatePasswordForRules } from '@heybesafe/shared';
import { HbsButton } from './Button';
import { HbsSwitch } from './Switch';
import { HbsStrengthMeter } from './StrengthMeter';
import { Icon } from './Icon';

interface HbsGeneratorProps {
  /** When provided, renders embed-mode CTAs ("Use this password" + "Cancel"). */
  onSelect?: (password: string) => void;
  /** Called when the user clicks "Cancel" in embed mode. */
  onClose?: () => void;
}

const DEFAULT_OPTIONS: PasswordOptions = {
  length: 20,
  uppercase: true,
  lowercase: true,
  numbers: true,
  symbols: true,
  excludeAmbiguous: true,
};

const SYMBOLS_FULL_SIZE = 26;
const SYMBOLS_UNAMBIGUOUS_SIZE = 25;

/**
 * Shannon entropy per character for the weighted-class generator
 * (PASSWORD_CLASS_WEIGHTS). Each enabled class contributes
 *   (w_i / W) * [log2(n_i) - log2(w_i / W)]
 * where w_i is its weight, W is the total enabled weight, and n_i
 * is the class's character-pool size after the ambiguous filter.
 *
 * Falls back to the lowercase + numbers buckets at canonical weights
 * when no class is enabled — mirrors the generator's own fallback.
 */
function bitsPerCharacter(options: PasswordOptions): number {
  // Compute enabled class pool sizes (post-ambiguous filter).
  const letters =
    (options.uppercase ? (options.excludeAmbiguous ? 24 : 26) : 0) +
    (options.lowercase ? (options.excludeAmbiguous ? 23 : 26) : 0);
  const numbers = options.numbers ? (options.excludeAmbiguous ? 8 : 10) : 0;
  const symbols = options.symbols
    ? (options.excludeAmbiguous ? SYMBOLS_UNAMBIGUOUS_SIZE : SYMBOLS_FULL_SIZE)
    : 0;

  const buckets: Array<[number, number]> = [];
  if (letters) buckets.push([letters, PASSWORD_CLASS_WEIGHTS.letters]);
  if (numbers) buckets.push([numbers, PASSWORD_CLASS_WEIGHTS.numbers]);
  if (symbols) buckets.push([symbols, PASSWORD_CLASS_WEIGHTS.symbols]);
  if (buckets.length === 0) {
    buckets.push([26, PASSWORD_CLASS_WEIGHTS.letters], [10, PASSWORD_CLASS_WEIGHTS.numbers]);
  }

  const total = buckets.reduce((sum, [, w]) => sum + w, 0);
  let h = 0;
  for (const [n, w] of buckets) {
    const p = w / total;
    h += p * (Math.log2(n) - Math.log2(p));
  }
  return h;
}

function strengthOf(password: string, options: PasswordOptions): number {
  const bits = bitsPerCharacter(options) * password.length;
  return Math.max(0, Math.min(1, (bits - 40) / 50));
}

function bitsOf(password: string, options: PasswordOptions): number {
  return Math.round(bitsPerCharacter(options) * password.length);
}

/**
 * Generate a password that actually contains every class the user ticked.
 *
 * `generatePassword` draws classes by weight (symbols only 10%), so it does not
 * guarantee an enabled class appears — a 16-character password omits symbols
 * about 18% of the time (0.9^16). The retry machinery already existed for SITE
 * rules; this routes the standalone generator through it too, treating the
 * checkboxes as the requirement. Bounded, so an impossible ask (more classes
 * than characters) returns best-effort instead of spinning.
 */
function generateHonouringOptions(options: PasswordOptions): string {
  return generatePasswordForRules(
    constraintsFromGeneratorOptions(options),
    () => generatePassword(options),
  );
}

export function HbsGenerator({ onSelect, onClose }: HbsGeneratorProps): JSX.Element {
  const [options, setOptions] = useState<PasswordOptions>(DEFAULT_OPTIONS);
  const [password, setPassword] = useState<string>(() => generateHonouringOptions(DEFAULT_OPTIONS));
  const sliderRef = useRef<HTMLInputElement>(null);

  const regenerate = useCallback(() => {
    setPassword(generateHonouringOptions(options));
  }, [options]);

  useEffect(() => {
    setPassword(generateHonouringOptions(options));
  }, [options]);

  // Native listener so raw DOM `input` events (e.g. from tests) also trigger
  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    function handleNativeInput(e: Event): void {
      const target = e.target as HTMLInputElement;
      const newLength = Number(target.value);
      setOptions((o) => ({ ...o, length: newLength }));
    }
    el.addEventListener('input', handleNativeInput);
    return () => el.removeEventListener('input', handleNativeInput);
  }, []);

  async function handleCopy(): Promise<void> {
    await navigator.clipboard.writeText(password);
  }

  function setOpt<K extends keyof PasswordOptions>(key: K, value: PasswordOptions[K]): void {
    setOptions((o) => ({ ...o, [key]: value }));
  }

  const strength = strengthOf(password, options);
  const bits = bitsOf(password, options);

  return (
    <div className="flex flex-col gap-5">
      {/* Generated card */}
      <section className="relative bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6 overflow-hidden">
        <Icon
          name="sparkle"
          size={180}
          className="absolute top-3 right-3 text-hbs-accent pointer-events-none"
          style={{ opacity: 0.05 }}
        />
        <div className="relative flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p
              data-testid="generated-password"
              className="font-mono text-[20px] tracking-hbs-mono text-hbs-text break-all leading-snug"
            >
              {password}
            </p>
            <div className="mt-3">
              <HbsStrengthMeter strength={strength} />
            </div>
            <p className="text-[12px] text-hbs-text-mute mt-1">
              {password.length} characters · {bits} bits of entropy
            </p>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <HbsButton variant="ghost" id="generator-regenerate-button" onClick={regenerate} aria-label="Regenerate">
              Regenerate
            </HbsButton>
            <HbsButton variant="primary" id="generator-copy-button" onClick={handleCopy}>
              Copy
            </HbsButton>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Length card */}
        <section className="bg-hbs-surface border border-hbs-border rounded-hbs-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
              Length
            </div>
            <span className="font-mono text-[14px] text-hbs-primary">{options.length}</span>
          </div>
          <input
            ref={sliderRef}
            id="generator-length"
            type="range"
            min={8}
            max={64}
            value={options.length}
            onChange={(e) => setOpt('length', Number(e.target.value))}
            aria-label="Length"
            className="w-full accent-hbs-primary"
          />
          <div className="flex justify-between text-[11px] text-hbs-text-mute mt-1">
            <span>8</span>
            <span>64</span>
          </div>
        </section>

        {/* Characters card */}
        <section className="bg-hbs-surface border border-hbs-border rounded-hbs-lg p-4">
          <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-3">
            Characters
          </div>
          <ul className="flex flex-col gap-3">
            {([
              ['uppercase', 'Uppercase (A–Z)'],
              ['lowercase', 'Lowercase (a–z)'],
              ['numbers', 'Numbers (0–9)'],
              ['symbols', 'Symbols (!@#$…)'],
              ['excludeAmbiguous', 'Exclude ambiguous (0, O, l, 1, I, |)'],
            ] as const).map(([key, label]) => (
              <li key={key} className="flex items-center justify-between gap-3">
                <span className="text-[13.5px] text-hbs-text">{label}</span>
                <HbsSwitch
                  checked={!!options[key]}
                  onChange={(v) => setOpt(key, v)}
                  label={label}
                />
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Embed-mode CTAs */}
      {onSelect && (
        <div className="flex flex-col gap-2 pt-2 border-t border-hbs-border">
          <HbsButton variant="primary" fullWidth id="generator-use-password-button" onClick={() => onSelect(password)}>
            Use this password
          </HbsButton>
          {onClose && (
            <HbsButton variant="ghost" fullWidth id="generator-cancel-button" onClick={onClose}>
              Cancel
            </HbsButton>
          )}
        </div>
      )}
    </div>
  );
}
